import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import AgentGrid from './AgentGrid'
import DotGrid from './DotGrid'
import PODashboard from './PODashboard'

export default function LeftPanel({ collapsed, onLogout, onCategoryClick, selectedGroup, onChatToggle, selectedAgent, onShipmentSelect, agentAutonomyMap }) {
  return (
    <motion.div
      layout
      transition={{ layout: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }}
      style={{
        ...(collapsed ? { width: 48, flexShrink: 0 } : { flex: 1, minWidth: 0 }),
        height: '100%',
        background: 'rgba(0,0,0,0.5)',
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <AnimatePresence mode="wait">
        {collapsed ? (
          <CollapsedBar key="collapsed" />
        ) : (
          <ExpandedContent
            key="expanded"
            onLogout={onLogout}
            onCategoryClick={onCategoryClick}
            selectedGroup={selectedGroup}
            onChatToggle={onChatToggle}
            selectedAgent={selectedAgent}
            onShipmentSelect={onShipmentSelect}
            agentAutonomyMap={agentAutonomyMap}
          />
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function ExpandedContent({ onLogout, onCategoryClick, selectedGroup, onChatToggle, selectedAgent, onShipmentSelect, agentAutonomyMap }) {
  const showDashboard = selectedGroup?.type === 'Purchase Orders'

  return (
    <motion.div
      key="expanded"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{ position: 'absolute', inset: 0, paddingTop: 16, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 24 }}
    >
      <DotGrid />
      <TopBar onLogout={onLogout} onChatToggle={onChatToggle} selectedAgent={selectedAgent} showDashboard={showDashboard} agentAutonomyMap={agentAutonomyMap} />
      {!showDashboard && <HeroText />}
      <AnimatePresence mode="wait">
        {showDashboard ? (
          <PODashboard key="po-dashboard" selectedAgent={selectedAgent} onShipmentSelect={onShipmentSelect} autonomyLevel={agentAutonomyMap?.['Status & Tracking'] ?? 0} />
        ) : (
          <AgentGrid key="agent-grid" onCategoryClick={onCategoryClick} />
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function HeroText() {
  return (
    <div style={{ position: 'relative', zIndex: 1, flexShrink: 0, paddingLeft: 32 }}>
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        style={{ fontSize: 24, fontWeight: 500, color: '#A39992', lineHeight: 1.15, margin: 0 }}
      >
        Good morning, Sarah
      </motion.p>
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
        style={{ fontSize: 32, fontWeight: 500, color: '#FFFFFF', lineHeight: 1.15, margin: 0, marginTop: 16 }}
      >
        You have 3 agents
        <br />
        that need attention
      </motion.p>
    </div>
  )
}

const AUTONOMY_INDEX_DEFAULTS = {
  'Orchestrator Agent':       0,
  'Status & Tracking':        0,
  'Creation & Replenishment': 1,
  'Validation':               1,
  'Exception Resolution':     0,
  'PO-to-Invoice Matching':   2,
}

const AUTONOMY_STYLES = [
  { label: 'Co-pilot',        color: '#60A5FA', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  { label: 'Semi-autonomous', color: '#A78BFA', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.3)' },
  { label: 'Autonomous',      color: '#4ADE80', bg: 'rgba(74,222,128,0.12)',  border: 'rgba(74,222,128,0.3)'  },
]

function AutonomyBadge({ agent, autonomyMap }) {
  const idx = autonomyMap?.[agent] ?? AUTONOMY_INDEX_DEFAULTS[agent] ?? 0
  const level = AUTONOMY_STYLES[idx]
  return (
    <motion.span
      key={idx}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      style={{
        fontSize: 9,
        fontWeight: 700,
        color: level.color,
        background: level.bg,
        border: `1px solid ${level.border}`,
        borderRadius: 4,
        padding: '2px 7px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        whiteSpace: 'nowrap',
      }}
    >
      {level.label}
    </motion.span>
  )
}

function CollapsedBar() {
  return (
    <motion.div
      key="collapsed"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, delay: 0.2 }}
      style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', paddingTop: 16, gap: 8,
      }}
    >
      {[<BoltIcon />, <ChatIcon />, <SearchIcon />].map((icon, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25 + i * 0.07, duration: 0.25 }}
          style={{
            width: 28, height: 28, borderRadius: 8,
            background: i === 0 ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {icon}
        </motion.div>
      ))}
    </motion.div>
  )
}

function TopBar({ onLogout, onChatToggle, selectedAgent, showDashboard, agentAutonomyMap }) {
  const date = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        height: 28, position: 'relative', zIndex: 1, flexShrink: 0,
      }}
    >
      <motion.div
        key={showDashboard && selectedAgent ? selectedAgent : 'date'}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.3 }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 32 }}
      >
        <span style={{
          fontSize: showDashboard && selectedAgent ? 14 : 10,
          fontWeight: 700,
          color: showDashboard && selectedAgent ? '#FFFFFF' : '#A39992',
          letterSpacing: showDashboard && selectedAgent ? 'normal' : '0.1em',
          textTransform: showDashboard && selectedAgent ? 'none' : 'uppercase',
        }}>
          {showDashboard && selectedAgent ? selectedAgent : date}
        </span>
        {showDashboard && selectedAgent && (
          <AutonomyBadge agent={selectedAgent} autonomyMap={agentAutonomyMap} />
        )}
      </motion.div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, paddingRight: 32 }}>
        {!showDashboard && (
          <>
            <motion.div
              animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ADE80', flexShrink: 0 }}
            />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Mercy General Hospital
            </span>
            <motion.button
              whileHover={{ scale: 1.12, background: 'rgba(255,255,255,0.18)' }}
              whileTap={{ scale: 0.92 }}
              transition={{ duration: 0.15 }}
              onClick={onLogout}
              style={{
                width: 28, height: 28, borderRadius: 8,
                background: 'rgba(0,0,0,0.3)',
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginRight: 24,
              }}
            >
              <LogoutIcon />
            </motion.button>
          </>
        )}
        {[
          { icon: <SearchIcon />, active: false, onClick: null },
          { icon: <ChatIcon />, active: false, onClick: onChatToggle },
          { icon: <BoltIcon />, active: true, onClick: null },
        ].map(({ icon, active, onClick }, i) => (
          <motion.button
            key={i}
            onClick={onClick ?? undefined}
            whileHover={{ scale: 1.12, background: 'rgba(255,255,255,0.18)' }}
            whileTap={{ scale: 0.92 }}
            transition={{ duration: 0.15 }}
            style={{
              width: 28, height: 28, borderRadius: 8,
              background: active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.3)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {icon}
          </motion.button>
        ))}
      </div>
    </motion.div>
  )
}


function SearchIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
      <circle cx="4.5" cy="4.5" r="3.2" stroke="rgba(255,255,255,0.7)" strokeWidth="1.3" />
      <line x1="6.9" y1="6.9" x2="9.5" y2="9.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M1.5 2.5h9a.5.5 0 01.5.5v5a.5.5 0 01-.5.5H4L1.5 10.5V3a.5.5 0 01.5-.5z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  )
}

function BoltIcon() {
  return (
    <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
      <path d="M6 1L1 7h4l-1 4 5-6H5l1-4z" fill="white" />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
      <path d="M4 2H2a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M7.5 3.5L10 5.5l-2.5 2" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="10" y1="5.5" x2="4.5" y2="5.5" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}
